/**
 * VÉRAULT Portal — My Collection (Wave 35 · Phase 1 · PI-6)
 *
 * Cross-trip library. Trusted hotels, activities, restaurants, guides,
 * transports, and unique experiences the advisor has vetted — saved
 * once, reusable across every future trip.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Library, Search, Plus, Trash2, Loader2, MapPin, Hotel, Compass, Utensils, User, Car, Sparkles, Wand2,
} from "lucide-react";
import {
  Surface,
  PortalButton,
  StatusTag,
  PageHeader,
  EditorialRule,
  AIWhisper,
  MetricTile,
  PortalBreadcrumb,
  api,
  motion as portalMotion,
} from "../../index";
import { toast } from "sonner";

const { fadeInUp, stagger } = portalMotion;

const KINDS = [
  { value: "hotel",      label: "Hotel",      icon: Hotel },
  { value: "activity",   label: "Activity",   icon: Compass },
  { value: "restaurant", label: "Restaurant", icon: Utensils },
  { value: "guide",      label: "Guide",      icon: User },
  { value: "transport",  label: "Transport",  icon: Car },
  { value: "experience", label: "Experience", icon: Sparkles },
];
const KIND_BY_VALUE = Object.fromEntries(KINDS.map((k) => [k.value, k]));

function emptyDraft() {
  return {
    kind: "hotel",
    name: "",
    location: "",
    description: "",
    highlights: "",
    contact: "",
    price_hint: "",
    notes: "",
    tags: "",
  };
}

function AddDialog({ open, onClose, onCreated }) {
  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiHint, setAiHint] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  if (!open) return null;

  const extract = async () => {
    if (!aiText.trim()) {
      toast.error("Paste some text first.");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/library/ai-extract", {
        text: aiText,
        hint: aiHint || null,
      });
      const d = data?.draft || {};
      setDraft({
        kind: d.kind || "hotel",
        name: d.name || "",
        location: d.location || "",
        description: d.description || "",
        highlights: (d.highlights || []).join("\n"),
        contact: d.contact || "",
        price_hint: d.price_hint || "",
        notes: "",
        tags: (d.tags || []).join(", "),
      });
      toast.success("Draft populated — review then save.");
      setAiOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't extract");
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/library", {
        kind: draft.kind,
        name: draft.name.trim(),
        location: draft.location.trim() || null,
        description: draft.description.trim() || null,
        highlights: draft.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
        contact: draft.contact.trim() || null,
        price_hint: draft.price_hint.trim() || null,
        notes: draft.notes.trim() || null,
        tags: draft.tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`${KIND_BY_VALUE[data.kind]?.label || data.kind} saved`);
      setDraft(emptyDraft());
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="library-add-dialog">
      <Surface variant="paper" padded className="!p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              Add to my collection
            </p>
            <p className="font-serif text-[18px] mt-1">Save once. Reuse everywhere.</p>
          </div>
          <button onClick={onClose} className="font-mono text-[12px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]">Close</button>
        </div>

        {/* AI extract section */}
        <Surface variant="paper" padded className="!p-3 mb-4 border-[hsl(var(--accent-gold))]/30" data-testid="library-ai-extract">
          {!aiOpen ? (
            <button
              onClick={() => setAiOpen(true)}
              className="w-full flex items-center justify-between gap-2 text-left"
              data-testid="library-ai-open"
            >
              <div className="flex items-center gap-2">
                <Wand2 className="size-3.5 text-[hsl(var(--accent-gold))]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                  Extract from text
                </span>
                <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                  Paste an email, brochure, or note — Claude fills the fields below.
                </span>
              </div>
              <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))]">+</span>
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Wand2 className="size-3.5 text-[hsl(var(--accent-gold))]" />
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                  Extract from text · Claude Sonnet 4.5
                </p>
              </div>
              <textarea
                value={aiText}
                onChange={(e) => setAiText(e.target.value)}
                rows={5}
                placeholder="Paste a confirmation email, brochure paragraph, supplier note, or website excerpt…"
                className="w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[12.5px]"
                data-testid="library-ai-text"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  value={aiHint}
                  onChange={(e) => setAiHint(e.target.value)}
                  placeholder="Optional hint (e.g. 'hotel' or 'private guide')"
                  className="flex-1 min-w-[200px] px-2 py-1.5 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[12px]"
                  data-testid="library-ai-hint"
                />
                <PortalButton variant="primary" size="sm" onClick={extract} disabled={aiBusy} data-testid="library-ai-extract-btn">
                  {aiBusy ? <Loader2 className="size-3.5 animate-spin" /> : <Wand2 className="size-3.5" />}
                  Extract draft
                </PortalButton>
                <PortalButton variant="ghost" size="sm" onClick={() => { setAiOpen(false); setAiText(""); setAiHint(""); }}>
                  Cancel
                </PortalButton>
              </div>
              <p className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))]">
                Nothing is saved until you click &ldquo;Save to collection&rdquo;. The extractor only populates the form below — you stay in control.
              </p>
            </div>
          )}
        </Surface>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Kind</span>
            <select
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-kind-select"
            >
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Location</span>
            <input
              value={draft.location}
              onChange={(e) => setDraft({ ...draft, location: e.target.value })}
              placeholder="Lisbon, Portugal"
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-location-input"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Name</span>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Six Senses Douro Valley"
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-name-input"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Description</span>
            <textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              rows={3}
              placeholder="One calm paragraph the advisor can lift straight into a proposal."
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-description-input"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Highlights (one per line)</span>
            <textarea
              value={draft.highlights}
              onChange={(e) => setDraft({ ...draft, highlights: e.target.value })}
              rows={3}
              placeholder="Riverside spa with vine-temperature pool&#10;Private vineyard tour with the cellar master"
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-highlights-input"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Contact</span>
            <input
              value={draft.contact}
              onChange={(e) => setDraft({ ...draft, contact: e.target.value })}
              placeholder="Reservations · reservations@example.com"
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-contact-input"
            />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Price hint</span>
            <input
              value={draft.price_hint}
              onChange={(e) => setDraft({ ...draft, price_hint: e.target.value })}
              placeholder="€450 / night"
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-price-input"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Tags (comma-separated)</span>
            <input
              value={draft.tags}
              onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
              placeholder="portugal, wine, spa, douro"
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-tags-input"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">Internal note (advisor only)</span>
            <textarea
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              rows={2}
              placeholder="Always ask for room 308 on the river side."
              className="mt-1 w-full px-3 py-2 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[13px]"
              data-testid="library-notes-input"
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <PortalButton variant="ghost" size="sm" onClick={onClose}>Cancel</PortalButton>
          <PortalButton variant="primary" size="sm" onClick={save} disabled={busy} data-testid="library-save">
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />} Save to collection
          </PortalButton>
        </div>
      </Surface>
    </div>
  );
}

function ItemCard({ item, onDelete }) {
  const meta = KIND_BY_VALUE[item.kind] || { label: item.kind, icon: Library };
  const Icon = meta.icon;
  const handleDelete = async () => {
    if (!window.confirm(`Remove "${item.name}" from your collection?`)) return;
    try {
      await api.delete(`/admin/library/${item.id}`);
      onDelete?.(item.id);
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <Surface variant="paper" padded className="!p-4" data-testid={`library-item-${item.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground-muted))] shrink-0">
          <Icon className="size-4" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
            {meta.label}{item.location ? ` · ${item.location}` : ""}
          </p>
          <p className="font-serif text-[17px] mt-0.5">{item.name}</p>
          {item.description && (
            <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] mt-1">
              {item.description}
            </p>
          )}
          {item.highlights?.length > 0 && (
            <ul className="mt-2 space-y-1">
              {item.highlights.slice(0, 4).map((h, i) => (
                <li key={i} className="font-body text-[12.5px] text-[hsl(var(--foreground-secondary))] flex items-start gap-1.5">
                  <span className="text-[hsl(var(--accent-gold))] mt-0.5">·</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {item.price_hint && <StatusTag tone="neutral">{item.price_hint}</StatusTag>}
            {item.times_used > 0 && <StatusTag tone="muted">Used {item.times_used}×</StatusTag>}
            {(item.tags || []).slice(0, 4).map((t) => (
              <span key={t} className="font-mono text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--foreground-muted))]">#{t}</span>
            ))}
          </div>
        </div>
        <div className="shrink-0">
          <PortalButton variant="ghost" size="sm" onClick={handleDelete} data-testid={`library-delete-${item.id}`}>
            <Trash2 className="size-3.5" />
          </PortalButton>
        </div>
      </div>
    </Surface>
  );
}

export default function MyCollection() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = [];
      if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      if (kindFilter) params.push(`kind=${encodeURIComponent(kindFilter)}`);
      const { data } = await api.get(`/admin/library${params.length ? "?" + params.join("&") : ""}`);
      setRows(data.rows || []);
      setCounts(data.counts_by_kind || {});
    } catch (e) {
      toast.error("Couldn't load library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [kindFilter]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
     
  }, [q]);

  const totalAll = useMemo(() => Object.values(counts).reduce((a, b) => a + b, 0), [counts]);

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6" data-testid="my-collection-page">
      <PortalBreadcrumb
        parent={{ to: "/portal/advisor", label: "Atelier" }}
        trail={[{ label: "My Collection" }]}
      />
      <PageHeader
        eyebrow="VÉRAULT · Library"
        title="My Collection"
        lede="Trusted places, kept in one quiet shelf. Drop into any future experience."
      />

      <motion.div variants={fadeInUp}>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {KINDS.map((k) => (
            <button
              key={k.value}
              onClick={() => setKindFilter((cur) => (cur === k.value ? null : k.value))}
              className={`text-left rounded border transition-colors p-3 ${
                kindFilter === k.value
                  ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10"
                  : "border-[hsl(var(--surface-hairline))] bg-[hsl(var(--background-paper))] hover:border-[hsl(var(--accent-gold))]/40"
              }`}
              data-testid={`library-kind-filter-${k.value}`}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">{k.label}</p>
              <p className="font-serif text-[18px] mt-0.5">{counts[k.value] || 0}</p>
            </button>
          ))}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded className="!p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Search className="size-4 text-[hsl(var(--foreground-muted))] shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, place, tag, or highlight…"
              className="flex-1 min-w-0 bg-transparent border-none outline-none font-mono text-[13px]"
              data-testid="library-search-input"
            />
            <PortalButton variant="primary" size="sm" onClick={() => setShowAdd(true)} data-testid="library-add-btn">
              <Plus className="size-3.5" /> Add to collection
            </PortalButton>
          </div>
        </Surface>
      </motion.div>

      {loading ? (
        <Surface variant="paper" padded>
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] flex items-center gap-2">
            <Loader2 className="size-3.5 animate-spin" /> Loading…
          </p>
        </Surface>
      ) : rows.length === 0 ? (
        <Surface variant="paper" padded>
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            {totalAll === 0
              ? "Your collection is empty. Add the places + people you'd recommend twice."
              : "Nothing matches that filter."}
          </p>
        </Surface>
      ) : (
        <motion.div variants={fadeInUp} className="space-y-2">
          {rows.map((item) => (
            <ItemCard key={item.id} item={item} onDelete={(id) => setRows((r) => r.filter((x) => x.id !== id))} />
          ))}
        </motion.div>
      )}

      <EditorialRule />
      <AIWhisper label="A quiet note">
        Adding to your collection saves a calm summary the advisor can paste straight into a proposal.
        The same item can sit on a hundred different trips &mdash; one source of truth.
      </AIWhisper>

      <AddDialog open={showAdd} onClose={() => setShowAdd(false)} onCreated={(it) => { setRows((r) => [it, ...r]); setCounts((c) => ({ ...c, [it.kind]: (c[it.kind] || 0) + 1 })); }} />
    </motion.div>
  );
}
